/**
 * QuantumFlowSync.AI - Session Management Page
 * View and manage active sessions with device info and location
 */

import React, { useState, useEffect } from 'react';
import { 
  Monitor, MapPin, Clock, Globe, Smartphone, Laptop,
  AlertTriangle, Check, X, RefreshCw, LogOut, Shield
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTier } from '../../hooks/usePermission';

const SessionManagementPage = () => {
  const { employee, apiRequest } = useAuth();
  const isSuperAdmin = useTier(1);
  
  // State
  const [sessions, setSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [viewMode, setViewMode] = useState('own'); // 'own' or 'all'
  
  // Fetch own sessions
  const fetchOwnSessions = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/admin/sessions');
      
      if (response.success) {
        setSessions(response.data.sessions || []);
      } else {
        setError(response.error || 'Failed to load sessions');
      }
    } catch (err) {
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch all sessions (Super Admin only)
  const fetchAllSessions = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/admin/sessions/all-employees');
      
      if (response.success) {
        setAllSessions(response.data.sessions || []);
      } else {
        setError(response.error || 'Failed to load all sessions');
      }
    } catch (err) {
      setError('Failed to load all sessions');
    } finally {
      setLoading(false);
    }
  };
  
  // Terminate specific session
  const terminateSession = async (sessionId) => {
    try {
      const response = await apiRequest(`/api/admin/sessions/${sessionId}`, {
        method: 'DELETE'
      });
      
      if (response.success) {
        setSuccess('Session terminated successfully');
        if (viewMode === 'own') {
          fetchOwnSessions();
        } else {
          fetchAllSessions();
        }
      } else {
        setError(response.error || 'Failed to terminate session');
      }
    } catch (err) {
      setError('Failed to terminate session');
    }
  };
  
  // Terminate all other sessions
  const terminateOtherSessions = async () => {
    if (!confirm('Are you sure you want to terminate all other sessions? You will remain logged in on this device.')) {
      return;
    }
    
    try {
      const response = await apiRequest('/api/admin/sessions/all', {
        method: 'DELETE'
      });
      
      if (response.success) {
        setSuccess('All other sessions terminated successfully');
        fetchOwnSessions();
      } else {
        setError(response.error || 'Failed to terminate sessions');
      }
    } catch (err) {
      setError('Failed to terminate sessions');
    }
  };
  
  // Force logout employee (Super Admin only)
  const forceLogoutEmployee = async (employeeId, employeeName) => {
    if (!confirm(`Are you sure you want to force logout ${employeeName}? All their active sessions will be terminated.`)) {
      return;
    }
    
    try {
      const response = await apiRequest(`/api/admin/sessions/employee/${employeeId}`, {
        method: 'DELETE'
      });
      
      if (response.success) {
        setSuccess(`Successfully logged out ${employeeName}`);
        fetchAllSessions();
      } else {
        setError(response.error || 'Failed to force logout');
      }
    } catch (err) {
      setError('Failed to force logout');
    }
  };
  
  // Get device icon
  const getDeviceIcon = (device) => {
    if (device.toLowerCase().includes('mobile') || device.toLowerCase().includes('phone')) {
      return <Smartphone className="w-5 h-5" />;
    }
    return <Laptop className="w-5 h-5" />;
  };
  
  // Format time ago
  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };
  
  // Effect: Fetch sessions on mount
  useEffect(() => {
    if (viewMode === 'own') {
      fetchOwnSessions();
    } else if (viewMode === 'all' && isSuperAdmin) {
      fetchAllSessions();
    }
  }, [viewMode]);
  
  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (viewMode === 'own') {
        fetchOwnSessions();
      } else if (viewMode === 'all' && isSuperAdmin) {
        fetchAllSessions();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [viewMode]);
  
  const displaySessions = viewMode === 'own' ? sessions : allSessions;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Session Management
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            View and manage active sessions
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => viewMode === 'own' ? fetchOwnSessions() : fetchAllSessions()}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          
          {viewMode === 'own' && (
            <button
              onClick={terminateOtherSessions}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Terminate Other Sessions
            </button>
          )}
        </div>
      </div>
      
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <span className="text-red-800 dark:text-red-200">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center">
          <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
          <span className="text-green-800 dark:text-green-200">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* View Mode Toggle (Super Admin only) */}
      {isSuperAdmin && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setViewMode('own')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'own'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <Monitor className="w-4 h-4 inline mr-2" />
              My Sessions
            </button>
            
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <Globe className="w-4 h-4 inline mr-2" />
              All Sessions (Super Admin)
            </button>
          </div>
        </div>
      )}
      
      {/* Sessions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
        </div>
      ) : displaySessions.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Monitor className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No active sessions found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {displaySessions.map((session) => (
            <div
              key={session.id}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 p-6 ${
                session.is_current
                  ? 'border-green-500 dark:border-green-600'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                    {getDeviceIcon(session.device)}
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {session.device}
                      </h3>
                      {session.is_current && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Current Session
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <Monitor className="w-4 h-4 mr-2" />
                        {session.browser} on {session.os}
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <MapPin className="w-4 h-4 mr-2" />
                        {session.location} ({session.ip_address})
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <Clock className="w-4 h-4 mr-2" />
                        Last active: {timeAgo(session.last_active)}
                      </div>
                      
                      {viewMode === 'all' && (
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <Shield className="w-4 h-4 mr-2" />
                          {session.employee_name} ({session.employee_email})
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {!session.is_current && (
                    <button
                      onClick={() => terminateSession(session.id)}
                      className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center gap-1"
                    >
                      <LogOut className="w-4 h-4" />
                      Terminate
                    </button>
                  )}
                  
                  {viewMode === 'all' && isSuperAdmin && (
                    <button
                      onClick={() => forceLogoutEmployee(session.employee_id, session.employee_name)}
                      className="px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm flex items-center gap-1"
                    >
                      <Shield className="w-4 h-4" />
                      Force Logout
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionManagementPage;
